import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { X, Globe, Lock } from 'lucide-react';
import { CredentialsManager } from './CredentialsManager';
import { ApiCredential } from '../../../auth/services/credentials.service';
import { API_CONFIG } from '../../../../config/constants';

interface Props {
  onClose: () => void;
}

const TOKEN_QUERY_KEY = /token/i;
const URL_DEBOUNCE_MS = 200;

export const SimpleApiForm: React.FC<Props> = ({ onClose }) => {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [useCredentials, setUseCredentials] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState<ApiCredential | null>(null);
  const [saving, setSaving] = useState(false);

  // Auth fields for inline config
  const [authType, setAuthType] = useState<'BEARER_TOKEN' | 'TOKEN_SIMPLE'>('BEARER_TOKEN');
  const [token, setToken] = useState('');
  const [tokenPlacementType, setTokenPlacementType] = useState<'header' | 'query'>('header');
  const [tokenHeaderName, setTokenHeaderName] = useState('Authorization');
  const [tokenHeaderFormat, setTokenHeaderFormat] = useState('Bearer {token}');
  const [tokenQueryParamName, setTokenQueryParamName] = useState('access_token');
  const [queryParams, setQueryParams] = useState<Array<{ id: string; key: string; value: string; enabled: boolean }>>([]);
  const urlDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enabledQueryParams = useMemo(() => {
    return queryParams.filter((param) => param.enabled && param.key.trim());
  }, [queryParams]);

  const queryParamsObject = useMemo(() => {
    return enabledQueryParams.reduce<Record<string, string>>((acc, param) => {
      const paramKey = param.key.trim();
      if (
        tokenPlacementType === 'query' &&
        tokenQueryParamName.trim() &&
        paramKey.toLowerCase() === tokenQueryParamName.trim().toLowerCase()
      ) {
        return acc;
      }
      acc[paramKey] = param.value;
      return acc;
    }, {});
  }, [enabledQueryParams, tokenPlacementType, tokenQueryParamName]);

  const syncQueryParamsFromUrl = useCallback((rawUrl: string) => {
    const trimmedUrl = rawUrl.trim();
    if (!trimmedUrl || !trimmedUrl.includes('?')) {
      return;
    }

    let params: Array<{ id: string; key: string; value: string; enabled: boolean }> = [];

    try {
      const urlObj = new URL(trimmedUrl.replace(/\{[^}]+\}/g, 'placeholder'));
      urlObj.searchParams.forEach((value, key) => {
        const isPlaceholder = value === 'placeholder' || value.includes('{') || value.includes('}');
        params.push({
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          key,
          value: isPlaceholder ? '' : value,
          enabled: true
        });
      });
    } catch {
      const match = trimmedUrl.match(/\?(.+)/);
      if (match) {
        const paramsStr = match[1];
        params = paramsStr.split('&').map((param) => {
          const [key, value] = param.split('=');
          const rawValue = value ?? '';
          const isPlaceholder = rawValue.includes('{') || rawValue.includes('}');
          return {
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            key: key || '',
            value: isPlaceholder ? '' : rawValue,
            enabled: true
          };
        });
      }
    }

    setQueryParams(params);

    const tokenParam = params.find((param) => TOKEN_QUERY_KEY.test(param.key));
    if (tokenParam) {
      setIsPrivate(true);
      setTokenPlacementType('query');
      setTokenQueryParamName(tokenParam.key);
    }
  }, []);

  useEffect(() => {
    if (urlDebounceRef.current) {
      clearTimeout(urlDebounceRef.current);
    }

    urlDebounceRef.current = setTimeout(() => {
      syncQueryParamsFromUrl(url);
    }, URL_DEBOUNCE_MS);

    return () => {
      if (urlDebounceRef.current) {
        clearTimeout(urlDebounceRef.current);
      }
    };
  }, [url, syncQueryParamsFromUrl]);

  const handleUrlBlur = () => {
    if (urlDebounceRef.current) {
      clearTimeout(urlDebounceRef.current);
    }
    syncQueryParamsFromUrl(url);
  };

  const addQueryParam = () => {
    setQueryParams((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        key: '',
        value: '',
        enabled: true
      }
    ]);
  };

  const updateQueryParam = (id: string, field: 'key' | 'value' | 'enabled', value: string | boolean) => {
    setQueryParams((prev) =>
      prev.map((param) => (param.id === id ? { ...param, [field]: value } : param))
    );
  };

  const removeQueryParam = (id: string) => {
    setQueryParams((prev) => prev.filter((param) => param.id !== id));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!url.trim()) {
      toast.error('La URL es requerida');
      return;
    }

    if (isPrivate && useCredentials && !selectedCredential) {
      toast.error('Selecciona una credencial');
      return;
    }

    if (isPrivate && !useCredentials && !token.trim()) {
      toast.error('El token es requerido');
      return;
    }

    setSaving(true);
    
    try {
      // 🔑 Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // 🧪 PASO 1: Test connection first to get sample data
      console.log('🧪 [SimpleApiForm] Testing API connection...');

      const tokenPlacement =
        isPrivate
          ? tokenPlacementType === 'query'
            ? { type: 'query', name: tokenQueryParamName.trim() || 'access_token' }
            : {
                type: 'header',
                name: tokenHeaderName.trim() || 'Authorization',
                format: tokenHeaderFormat.trim() || 'Bearer {token}'
              }
          : undefined;

      const authPlacement =
        isPrivate
          ? tokenPlacementType === 'query'
            ? { placement: 'query', queryParam: tokenQueryParamName.trim() || 'access_token' }
            : {
                placement: 'header',
                headerName: tokenHeaderName.trim() || 'Authorization',
                format: tokenHeaderFormat.trim() || 'Bearer {token}'
              }
          : undefined;
      
      const testResponse = await fetch(`${API_CONFIG.BASE_URL}/api/apirest/test`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // 🔑 Include auth token
        },
        body: JSON.stringify({
          auth: isPrivate && useCredentials && selectedCredential
            ? { type: 'credential', credentialId: selectedCredential.id, ...authPlacement }
            : isPrivate && !useCredentials && token
            ? { type: authType, token: token, ...authPlacement }
            : { type: 'none' },
          request: {
            method: 'GET',
            baseUrl: url.trim(),
            endpoint: '/',
            headers: {},
            queryParams: Object.keys(queryParamsObject).length > 0 ? queryParamsObject : undefined
          }
        })
      });

      const testData = await testResponse.json();

      console.log('🔍 [SimpleApiForm] Test response:', testData);

      if (!testResponse.ok || !testData.success) {
        console.error('❌ [SimpleApiForm] Test failed:', testData);
        throw new Error(testData.error || 'Error al probar la conexión API');
      }

      console.log('✅ [SimpleApiForm] API test successful, got sample data');
      
      // Validar que tenemos los datos necesarios
      if (!testData.data || !testData.data.normalizedData || !testData.data.schema) {
        console.error('❌ [SimpleApiForm] Missing data structure:', testData);
        throw new Error('Respuesta de test incompleta: faltan datos normalizados');
      }
      
      console.log(`📊 [SimpleApiForm] Got ${testData.data.normalizedData.length} rows`);
      
      // 💾 PASO 2: Save with sample data
      const payload = {
        name: name.trim(),
        type: 'API_REST',
        apiType: isPrivate ? 'PRIVATE' : 'PUBLIC',
        requestConfig: {
          method: 'GET',
          baseUrl: url.trim(),
          endpoint: '/',
          ...(Object.keys(queryParamsObject).length > 0 ? { queryParams: queryParamsObject } : {}),
          ...(tokenPlacement ? { tokenPlacement } : {})
        },
        // 🎯 CRITICAL: Include sample data from test
        sampleData: testData.data.normalizedData,
        schema: testData.data.schema,
        arrayPath: testData.data.schema.detectedArrayPath || 'root',
        // Support both credential reference and embedded auth config
        ...(isPrivate && useCredentials && selectedCredential
          ? { apiCredentialId: selectedCredential.id }
          : isPrivate && !useCredentials && token
          ? { 
              authConfig: {
                mode: authType,
                [authType === 'BEARER_TOKEN' ? 'bearerToken' : 'tokenValue']: token
              }
            }
          : {}
        ),
        autoAnalyze: true
      };

      // 🚨 DEBUG: Log payload antes de enviar
      console.log('🚨 [SimpleApiForm] PAYLOAD COMPLETO:', JSON.stringify(payload, null, 2));
      console.log('🚨 [SimpleApiForm] selectedCredential:', selectedCredential);
      console.log('🚨 [SimpleApiForm] useCredentials:', useCredentials);

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/apirest/save`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // 🔑 Include auth token
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Error al guardar el datasource');
      }

      toast.success('API guardada correctamente');
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-800 rounded-lg w-full max-w-2xl mx-auto max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-slate-700">
        <div>
          <h2 className="text-xl font-bold text-white">Nueva API REST</h2>
          <p className="text-sm text-slate-400 mt-1">Configura una fuente de datos de API</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Form */}
      <div className="p-6 space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Nombre
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Mi API de Ventas"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              URL de la API
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={handleUrlBlur}
              placeholder="https://api.example.com/data"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-300">
              Query parameters
            </label>
            <div className="space-y-2">
              {queryParams.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No hay parámetros. Agrega los necesarios para tu API.
                </p>
              ) : (
                queryParams.map((param) => (
                  <div key={param.id} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-1 flex justify-center">
                      <input
                        type="checkbox"
                        checked={param.enabled}
                        onChange={(e) => updateQueryParam(param.id, 'enabled', e.target.checked)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 checked:bg-blue-500"
                      />
                    </div>
                    <div className="col-span-5">
                      <input
                        type="text"
                        value={param.key}
                        onChange={(e) => updateQueryParam(param.id, 'key', e.target.value)}
                        placeholder="key"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="col-span-5">
                      <input
                        type="text"
                        value={param.value}
                        onChange={(e) => updateQueryParam(param.id, 'value', e.target.value)}
                        placeholder="value"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <button
                        type="button"
                        onClick={() => removeQueryParam(param.id)}
                        className="text-slate-400 hover:text-red-400 transition"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <button
              type="button"
              onClick={addQueryParam}
              className="text-blue-400 hover:text-blue-300 text-sm font-medium"
            >
              + Agregar parámetro
            </button>
          </div>
        </div>

        {/* API Type Toggle */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-slate-300">
            Tipo de API
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => setIsPrivate(false)}
              className={`flex-1 p-4 border-2 rounded-lg transition-colors ${
                !isPrivate 
                  ? 'border-green-500 bg-green-500/10 text-green-400' 
                  : 'border-slate-600 text-slate-400 hover:border-slate-500'
              }`}
            >
              <Globe className="w-5 h-5 mx-auto mb-2" />
              <div className="font-medium">Pública</div>
              <div className="text-xs opacity-75">Sin autenticación</div>
            </button>

            <button
              onClick={() => setIsPrivate(true)}
              className={`flex-1 p-4 border-2 rounded-lg transition-colors ${
                isPrivate 
                  ? 'border-blue-500 bg-blue-500/10 text-blue-400' 
                  : 'border-slate-600 text-slate-400 hover:border-slate-500'
              }`}
            >
              <Lock className="w-5 h-5 mx-auto mb-2" />
              <div className="font-medium">Privada</div>
              <div className="text-xs opacity-75">Requiere autenticación</div>
            </button>
          </div>
        </div>

        {/* Auth Section for Private APIs */}
        {isPrivate && (
          <div className="space-y-4 pt-4 border-t border-slate-700">
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="use-inline"
                name="auth-method"
                checked={!useCredentials}
                onChange={() => setUseCredentials(false)}
                className="w-4 h-4"
              />
              <label htmlFor="use-inline" className="text-sm text-slate-300">
                Configurar token directamente
              </label>
            </div>
            
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="use-saved"
                name="auth-method"
                checked={useCredentials}
                onChange={() => setUseCredentials(true)}
                className="w-4 h-4"
              />
              <label htmlFor="use-saved" className="text-sm text-slate-300">
                Usar credenciales guardadas
              </label>
            </div>

            {!useCredentials ? (
              <div className="space-y-3 bg-slate-750 p-4 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Tipo de Token
                  </label>
                  <select
                    value={authType}
                    onChange={(e) => setAuthType(e.target.value as any)}
                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="BEARER_TOKEN">Bearer Token</option>
                    <option value="TOKEN_SIMPLE">API Key Simple</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Token / API Key
                  </label>
                  <input
                    type="password"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="tu_token_aqui"
                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-300">
                    Inyectar token en
                  </label>
                  <select
                    value={tokenPlacementType}
                    onChange={(e) => setTokenPlacementType(e.target.value as 'header' | 'query')}
                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="header">Header</option>
                    <option value="query">Query Param</option>
                  </select>
                </div>
                {tokenPlacementType === 'header' ? (
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Header
                      </label>
                      <input
                        type="text"
                        value={tokenHeaderName}
                        onChange={(e) => setTokenHeaderName(e.target.value)}
                        placeholder="Authorization"
                        className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Formato
                      </label>
                      <input
                        type="text"
                        value={tokenHeaderFormat}
                        onChange={(e) => setTokenHeaderFormat(e.target.value)}
                        placeholder="Bearer {token}"
                        className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Nombre del query param
                    </label>
                    <input
                      type="text"
                      value={tokenQueryParamName}
                      onChange={(e) => setTokenQueryParamName(e.target.value)}
                      placeholder="access_token"
                      className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <p className="text-xs text-slate-400">
                  Tip: si tu API espera el token en la URL, elige "Query Param".
                </p>
              </div>
            ) : (
              <div className="bg-slate-750 p-4 rounded-lg">
                <div className="space-y-3 mb-4">
                  <label className="block text-sm font-medium text-slate-300">
                    Inyectar token en
                  </label>
                  <select
                    value={tokenPlacementType}
                    onChange={(e) => setTokenPlacementType(e.target.value as 'header' | 'query')}
                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="header">Header</option>
                    <option value="query">Query Param</option>
                  </select>
                  {tokenPlacementType === 'header' ? (
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Header
                        </label>
                        <input
                          type="text"
                          value={tokenHeaderName}
                          onChange={(e) => setTokenHeaderName(e.target.value)}
                          placeholder="Authorization"
                          className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Formato
                        </label>
                        <input
                          type="text"
                          value={tokenHeaderFormat}
                          onChange={(e) => setTokenHeaderFormat(e.target.value)}
                          placeholder="Bearer {token}"
                          className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Nombre del query param
                      </label>
                      <input
                        type="text"
                        value={tokenQueryParamName}
                        onChange={(e) => setTokenQueryParamName(e.target.value)}
                        placeholder="access_token"
                        className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </div>
                <CredentialsManager
                  mode="select"
                  onSelectCredential={setSelectedCredential}
                  selectedCredentialId={selectedCredential?.id}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 p-6 border-t border-slate-700">
        <button
          onClick={onClose}
          className="px-6 py-3 text-slate-400 hover:text-white transition-colors"
        >
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              Probando y guardando...
            </span>
          ) : 'Guardar API'}
        </button>
      </div>
    </div>
  );
};
